import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Badge, Card, CardBody } from '@img/ui';
import { api, type Paginated, type ApiSuccess } from '../lib/api';
import { SEO } from '../components/SEO';
import { KEYWORDS_BROKERS } from '../lib/seo-keywords';

interface BrokerListItem {
  id: string;
  name: string;
  image: string | null;
  city: string | null;
  bio: string | null;
  verificationTier: string | null;
  brokerProfile: {
    licenseNumber: string | null;
    agencyName: string | null;
    yearsExperience: number | null;
    totalDeals: number | null;
    rating: number | string | null;
    reviewsCount: number | null;
    verifiedBadge: boolean | null;
    subscriptionPlan: string | null;
  } | null;
}

interface PlanRow {
  id: string;
  code: string;
  category: 'INDIVIDUAL' | 'COMPANY';
  label: string;
  description: string | null;
  monthlyPrice: number | string;
  yearlyPrice: number | string | null;
  monthlyListingsQuota: number;
  featuredListingsQuota: number;
  subAccountsQuota: number;
  commissionShare: number | string;
  features: string[] | null;
  highlight: boolean;
  active: boolean;
  sortOrder: number;
}

export function BrokersPage() {
  const [tab, setTab] = useState<'INDIVIDUAL' | 'COMPANY'>('INDIVIDUAL');
  const [billing, setBilling] = useState<'monthly' | 'yearly'>('monthly');

  // 💎 الباقات
  const { data: plansResp } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: () => api.get<ApiSuccess<PlanRow[]>>('/api/subscription-plans'),
    staleTime: 5 * 60_000,
  });
  const allPlans = (plansResp?.data ?? []).filter((p) => p.active);
  const plans = allPlans
    .filter((p) => p.category === tab)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // 👥 المسوّقون (للقسم السفلي)
  const { data: brokersResp } = useQuery({
    queryKey: ['public-brokers'],
    queryFn: () => api.get<Paginated<BrokerListItem>>('/api/brokers', { query: { limit: 12 } }),
  });
  const brokers = brokersResp?.data ?? [];

  return (
    <div className="bg-gray-50 min-h-screen" dir="rtl">
      <SEO
        title="منصة التسويق العقاري — وسطاء ومسوقون معتمدون 👤 العقار بصورة جديدة"
        description="العقار بصورة جديدة — انضم لمنصة التسويق العقاري الأولى في السعودية. باقات مخصّصة للمسوّقين الأفراد وشركات التسويق العقاري، ترخيص فال، وأدوات احترافية لنشر إعلانات عقارية مرخّصة من الهيئة العامة للعقار. اكتشف أفضل الوسطاء العقاريين المعتمدين في الرياض وجدة والدمام ومكة المكرمة. شركة صورة العقارية — العقار بصورة جديدة."
        keywords={KEYWORDS_BROKERS}
        canonical="https://img.sa/brokers"
        jsonLd={{
          '@type': 'Service',
          name: 'منصة التسويق العقاري — العقار بصورة جديدة',
          description: 'العقار بصورة جديدة — اكتشف وسطاء عقاريين معتمدين بترخيص فال في كل مدن السعودية',
          provider: {
            '@type': 'Organization',
            name: 'شركة صورة العقارية',
            slogan: 'العقار بصورة جديدة',
            url: 'https://img.sa',
          },
          serviceType: 'Real Estate Brokerage Platform',
          areaServed: { '@type': 'Country', name: 'Saudi Arabia' },
          url: 'https://img.sa/brokers',
        }}
      />
      {/* ━━━━━━━━━━━━━━━━━━━━ HERO ━━━━━━━━━━━━━━━━━━━━ */}
      <section className="relative overflow-hidden bg-gradient-to-br from-navy-darker via-navy-dark to-navy text-white">
        {/* تأثيرات خلفية */}
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-gold/10 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-navy-light/15 blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-4 py-20 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-1.5 rounded-full text-xs font-bold mb-5">
            <span>📣</span>
            <span>منصة معتمدة وفق أنظمة الهيئة العامة للعقار (REGA)</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black mb-4 leading-tight">
            منصة التسويق العقاري
          </h1>
          <p className="text-lg md:text-xl text-white/85 max-w-2xl mx-auto mb-8 leading-relaxed">
            انضم لأكبر منصة عقارية رقمية في المملكة. احصل على عملاء جاهزين، أدوات احترافية، وعمولة تصل لـ 88%.
          </p>
          <div className="flex gap-3 flex-wrap justify-center">
            <a href="#plans">
              <Button variant="gold" size="lg">📋 اختر باقتك</Button>
            </a>
            <Link to="/become-broker">
              <Button variant="outline" size="lg" className="!bg-white/10 !text-white !border-white/40 hover:!bg-white/20">
                📤 سجّل كمسوّق
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━ شريط الإحصاءات ━━━━━━━━━━━━━━━━━━━━ */}
      <section className="bg-gradient-to-r from-gold via-amber-500 to-gold-dark py-8">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <Stat value="88%" label="تصل العمولة إلى" />
          <Stat value="13" label="منطقة في المملكة" />
          <Stat value="REGA" label="معتمدون رسمياً" />
          <Stat value="٢٤/٧" label="دعم متواصل" />
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━ لماذا تنضم؟ ━━━━━━━━━━━━━━━━━━━━ */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <h2 className="text-3xl md:text-4xl font-black text-navy-darker text-center mb-3">
          لماذا منصة التسويق العقاري؟
        </h2>
        <p className="text-ink-secondary text-center mb-10">منصة شاملة تخدم المسوّقين الأفراد والشركات على حدّ سواء</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Reason icon="💰" title="عمولة عالية" desc="حتى 88% لباقات الشركات و82% للأفراد، الأعلى في السوق." />
          <Reason icon="🎯" title="عملاء جاهزون" desc="نوصلك بطالبي العقارات الفعليين بدون عناء بحث." />
          <Reason icon="📊" title="إحصائيات احترافية" desc="تتبّع زيارات إعلاناتك، التواصل، والمعدلات لحظياً." />
          <Reason icon="🪪" title="رخصة فال موثّقة" desc="نتحقق من رخصتك ونعرض شارة الاعتماد على إعلاناتك." />
          <Reason icon="🗺️" title="ظهور على الخريطة" desc="إعلاناتك تظهر بدبابيس بارزة على خريطة العقارات." />
          <Reason icon="🔨" title="حضور مزادات حية" desc="انضم لغرفة المزادات المباشرة وأظهر مهاراتك." />
          <Reason icon="🏢" title="حسابات فرعية" desc="للشركات: لوحة موحّدة لإدارة فريق المسوّقين بأكمله." />
          <Reason icon="🛡️" title="حماية قانونية" desc="عقود معتمدة من REGA و حماية من الشكاوى الكيدية." />
          <Reason icon="🚀" title="تسويق ذكي" desc="إعلانات مدفوعة، حملات SMS وWhatsApp، وإشعارات للعملاء." />
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━ الباقات ━━━━━━━━━━━━━━━━━━━━ */}
      <section id="plans" className="py-16 bg-white border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-black text-navy-darker text-center mb-3">
            باقات الاشتراك
          </h2>
          <p className="text-ink-secondary text-center mb-8">اختر الباقة الأنسب لاحتياجك. ترقية أو إلغاء في أي وقت.</p>

          {/* تبديل الفئة */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex bg-gray-100 rounded-2xl p-1.5 gap-1">
              <button
                onClick={() => setTab('INDIVIDUAL')}
                className={`px-6 py-2.5 rounded-xl text-sm font-black transition ${
                  tab === 'INDIVIDUAL'
                    ? 'bg-navy-dark text-white shadow'
                    : 'text-ink-muted hover:text-navy-dark'
                }`}
              >
                👤 مسوّق فرد
              </button>
              <button
                onClick={() => setTab('COMPANY')}
                className={`px-6 py-2.5 rounded-xl text-sm font-black transition ${
                  tab === 'COMPANY'
                    ? 'bg-navy-dark text-white shadow'
                    : 'text-ink-muted hover:text-navy-dark'
                }`}
              >
                🏢 شركة تسويق
              </button>
            </div>
          </div>

          {/* تبديل شهري/سنوي */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex bg-gray-50 border border-gray-200 rounded-xl p-1 gap-1 text-xs">
              <button
                onClick={() => setBilling('monthly')}
                className={`px-4 py-1.5 rounded-lg font-bold transition ${
                  billing === 'monthly' ? 'bg-white shadow text-navy-dark' : 'text-ink-muted'
                }`}
              >
                شهري
              </button>
              <button
                onClick={() => setBilling('yearly')}
                className={`px-4 py-1.5 rounded-lg font-bold transition flex items-center gap-1.5 ${
                  billing === 'yearly' ? 'bg-white shadow text-navy-dark' : 'text-ink-muted'
                }`}
              >
                سنوي
                <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-black">وفّر شهرين</span>
              </button>
            </div>
          </div>

          {/* الباقات */}
          {plans.length === 0 ? (
            <div className="text-center text-ink-muted py-12">⟳ جاري تحميل الباقات…</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
              {plans.map((p) => <PlanCard key={p.id} plan={p} billing={billing} />)}
            </div>
          )}

          <p className="text-center text-xs text-ink-muted mt-8">
            * جميع الأسعار قبل ضريبة القيمة المضافة (15%) — وفقاً لأنظمة هيئة الزكاة والضريبة.
          </p>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━ كيف نبدأ؟ ━━━━━━━━━━━━━━━━━━━━ */}
      <section className="py-16 max-w-6xl mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-black text-navy-darker text-center mb-10">
          كيف تبدأ؟
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Step n={1} title="سجّل حسابك" desc="إنشاء حساب على المنصة في دقيقة." />
          <Step n={2} title="ارفع رخصة فال" desc="نتحقق من رخصتك خلال 24 ساعة." />
          <Step n={3} title="اختر باقتك" desc="حدّد الخطة المناسبة وادفع بأمان." />
          <Step n={4} title="ابدأ التسويق" desc="انشر عقاراتك واستقبل العملاء." />
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━ مسوّقون مميّزون ━━━━━━━━━━━━━━━━━━━━ */}
      {brokers.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
              <div>
                <h2 className="text-3xl font-black text-navy-darker mb-2">⭐ مسوّقونا المميّزون</h2>
                <p className="text-ink-secondary">نخبة من المسوّقين العقاريين المعتمدين</p>
              </div>
              <Link to="/brokers" className="text-sm font-black text-navy-dark hover:text-gold">
                عرض الكل ←
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {brokers.slice(0, 6).map((b) => <BrokerCard key={b.id} broker={b} />)}
            </div>
          </div>
        </section>
      )}

      {/* ━━━━━━━━━━━━━━━━━━━━ CTA ━━━━━━━━━━━━━━━━━━━━ */}
      <section className="py-20 bg-gradient-to-br from-navy-darker to-navy-dark text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-black mb-4">
            جاهز للانطلاق؟ ابدأ مجاناً اليوم
          </h2>
          <p className="text-white/80 text-lg mb-8">
            انضم لأكثر من ١٠٠+ مسوّق عقاري ينمو معنا كل يوم.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link to="/become-broker">
              <Button variant="gold" size="lg">📤 قدّم طلبك الآن</Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" size="lg" className="!bg-white/10 !text-white !border-white/40 hover:!bg-white/20">
                💬 تحدّث مع فريق المبيعات
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ مكوّنات فرعية ━━━━━━━━━━━━━━━━━━━━

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <div className="text-3xl md:text-4xl font-black text-navy-darker mb-1">{value}</div>
      <div className="text-xs md:text-sm text-navy-darker/80 font-bold">{label}</div>
    </div>
  );
}

function Reason({ icon, title, desc }: { icon: string; title: string; desc: string }) {
  return (
    <div className="bg-white rounded-2xl p-5 border-2 border-gray-100 hover:border-gold hover:shadow-md transition">
      <div className="text-3xl mb-2">{icon}</div>
      <h3 className="font-black text-navy-darker mb-1.5 text-base">{title}</h3>
      <p className="text-sm text-ink-secondary leading-relaxed">{desc}</p>
    </div>
  );
}

function Step({ n, title, desc }: { n: number; title: string; desc: string }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100 text-center">
      <div className="w-12 h-12 rounded-full bg-gold text-navy-darker font-black text-lg flex items-center justify-center mx-auto mb-3">
        {n}
      </div>
      <h3 className="font-black text-navy-darker mb-1.5">{title}</h3>
      <p className="text-xs text-ink-secondary leading-relaxed">{desc}</p>
    </div>
  );
}

function PlanCard({ plan, billing }: { plan: PlanRow; billing: 'monthly' | 'yearly' }) {
  const monthly = Number(plan.monthlyPrice);
  const yearly = plan.yearlyPrice ? Number(plan.yearlyPrice) : null;
  const price = billing === 'yearly' && yearly ? yearly : monthly;
  const isYearly = billing === 'yearly' && yearly;

  return (
    <div
      className={`relative rounded-2xl border-2 transition flex flex-col h-full ${
        plan.highlight
          ? 'border-gold bg-gradient-to-br from-white to-gold/5 shadow-xl md:scale-[1.03] z-10'
          : 'border-gray-200 bg-white hover:border-gold/50 hover:shadow-md'
      }`}
    >
      {plan.highlight && (
        <div className="absolute -top-3 right-1/2 translate-x-1/2 bg-gold text-navy-darker px-4 py-1 rounded-full text-xs font-black shadow">
          ⭐ الأكثر طلباً
        </div>
      )}

      <div className="p-6 flex flex-col flex-1">
        {/* الترويسة (ارتفاع ثابت) */}
        <div className="min-h-[88px]">
          <h3 className="font-black text-navy-darker text-xl mb-1">{plan.label}</h3>
          {plan.description && <p className="text-xs text-ink-secondary leading-relaxed">{plan.description}</p>}
        </div>

        {/* السعر (ارتفاع ثابت) */}
        <div className="mb-5 min-h-[80px]">
          {price === 0 ? (
            <div className="text-3xl font-black text-emerald-600">مجاناً</div>
          ) : (
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-black text-navy-darker">{price.toLocaleString('en-US')}</span>
              <span className="text-sm text-ink-muted font-bold">ر.س</span>
              <span className="text-xs text-ink-muted mr-1">/ {isYearly ? 'سنة' : 'شهر'}</span>
            </div>
          )}
          {isYearly && monthly > 0 && (
            <div className="text-[11px] text-emerald-600 font-bold mt-1">
              ⚡ توفير {(monthly * 12 - yearly!).toLocaleString('en-US')} ر.س
            </div>
          )}
        </div>

        {/* المزايا — تأخذ المساحة المتبقية */}
        <ul className="space-y-2 mb-6 text-sm flex-1">
          {(plan.features ?? []).map((f, i) => (
            <li key={i} className="flex items-start gap-2 text-ink-secondary">
              <span className="text-emerald-500 mt-0.5">✓</span>
              <span>{f}</span>
            </li>
          ))}
        </ul>

        {/* الزر — يلتصق بالأسفل دائماً */}
        <Link to={`/dashboard/subscription?plan=${plan.code}`} className="block mt-auto">
          <Button
            variant={plan.highlight ? 'gold' : 'primary'}
            size="lg"
            className="w-full"
          >
            {price === 0 ? 'ابدأ مجاناً' : 'اشترك الآن'}
          </Button>
        </Link>
      </div>
    </div>
  );
}

function BrokerCard({ broker }: { broker: BrokerListItem }) {
  return (
    <Link to={`/brokers/${broker.id}`} className="block">
      <Card hover>
        <CardBody>
          <div className="flex gap-3 items-start">
            <div className="w-14 h-14 rounded-2xl bg-navy-soft flex items-center justify-center text-2xl shrink-0">
              {broker.image ? (
                <img src={broker.image} alt={broker.name} className="w-full h-full object-cover rounded-2xl" />
              ) : '👤'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-black text-navy-darker truncate">{broker.name}</div>
              {broker.brokerProfile?.agencyName && (
                <div className="text-xs text-ink-muted truncate">{broker.brokerProfile.agencyName}</div>
              )}
              <div className="flex items-center gap-1.5 mt-1.5">
                {broker.brokerProfile?.verifiedBadge && <Badge color="green">✓ موثّق</Badge>}
                {broker.city && <span className="text-xs text-ink-muted">📍 {broker.city}</span>}
              </div>
              {broker.brokerProfile?.rating && Number(broker.brokerProfile.rating) > 0 && (
                <div className="text-xs text-ink-secondary mt-1.5">
                  ⭐ {Number(broker.brokerProfile.rating).toFixed(1)}
                  {broker.brokerProfile.reviewsCount ? ` (${broker.brokerProfile.reviewsCount})` : ''}
                </div>
              )}
            </div>
          </div>
        </CardBody>
      </Card>
    </Link>
  );
}
